import { memo } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form'
import { StyleProp, View, ViewStyle } from 'react-native'
import { TextInputProps } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import { getErrorField } from '@/components/form/formUtils'
import TextInput from '@/components/form/TextInput'
import Skeleton from '@/components/shared/Skeleton'
import UniHelperText from '@/components/unistyles/UniHelperText'

interface Props<C extends FieldValues, E extends FieldErrors> {
  fieldName: Path<C>
  required: boolean
  errors?: E
  control: Control<C, any>
  isLoading?: boolean
  helperText?: string
  contentStyle?: StyleProp<ViewStyle>
  onBlur?: () => void
}

const FormFieldText = <C extends FieldValues, E extends FieldErrors>({
  fieldName,
  required,
  errors,
  control,
  isLoading,
  style,
  helperText,
  onBlur,
  contentStyle,
  ...props
}: Props<C, E> & TextInputProps) => {
  return (
    <View style={[style as ViewStyle, styles.root]}>
      <Skeleton isLoading={isLoading}>
        <Controller
          control={control}
          name={fieldName}
          render={({ field: { onChange, onBlur: formOnBlur, value } }) => (
            <View style={styles.container}>
              <TextInput
                accessibilityHint="This is a text input field"
                accessibilityLabel="Text input field"
                testID={`${fieldName}-textInput`}
                {...props}
                contentStyle={[styles.container, contentStyle]}
                editable={!props.disabled}
                error={!!getErrorField(fieldName, errors)}
                label={props.label as string}
                multiline={props.multiline}
                onBlur={() => {
                  onBlur?.()
                  formOnBlur()
                }}
                onChangeText={onChange}
                value={value?.toString() || ''}
              />
              {getErrorField(fieldName, errors) && (
                <UniHelperText
                  type="error"
                  visible={!!getErrorField(fieldName, errors)}
                >
                  {getErrorField(fieldName, errors)?.message as string}
                </UniHelperText>
              )}
              {helperText && (
                <UniHelperText
                  style={styles.helperText}
                  type="info"
                  visible={!!helperText}
                >
                  {helperText}
                </UniHelperText>
              )}
            </View>
          )}
          rules={{
            required: required,
          }}
        />
      </Skeleton>
    </View>
  )
}

const styles = StyleSheet.create((theme) => ({
  container: {
    flexWrap: 'nowrap',
  },
  helperText: {
    color: theme.colors.subtitle,
    lineHeight: 16,
    paddingTop: theme.tokens.spacing[2],
  },
  root: {
    marginBottom: theme.tokens.spacing[2],
  },
}))

export default memo(FormFieldText) as typeof FormFieldText
